import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { AdminRole } from '../services/auth';

interface Props {
  children: React.ReactElement;
  /** Restrict to super admins, e.g. the account-management screen. */
  superAdminOnly?: boolean;
  /**
   * Restrict to specific roles. Used to keep marketers out of the content
   * tools — the server enforces the same rule, this only saves them a 403.
   */
  roles?: AdminRole[];
}

/**
 * Gates an admin route. While the stored token is being verified it renders a
 * spinner rather than redirecting, so a valid session never flashes the login
 * screen on refresh.
 */
const ProtectedRoute: React.FC<Props> = ({
  children,
  superAdminOnly = false,
  roles,
}) => {
  const { admin, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (!admin) {
    return <Navigate to="/admin/login" replace state={{ from: location.pathname }} />;
  }

  if (superAdminOnly && admin.role !== 'superadmin') {
    return <Navigate to="/admin" replace />;
  }

  if (roles && !roles.includes(admin.role)) {
    // Marketers land on their own board rather than a dashboard they cannot use.
    return (
      <Navigate
        to={admin.role === 'marketer' ? '/admin/partners/prospects' : '/admin'}
        replace
      />
    );
  }

  return children;
};

export default ProtectedRoute;
